import { BusinessObjectBase, decodeDecimals } from './base';
import { npmPackageSchema } from '@schemas/business-objects/npm-package';
import type { PackageDoc } from './package-doc';
import type { PackageVersion } from './package-version';
import type { RegistryToken } from './registry-token';
import type { Id, PackageAccess, PackageCategory } from '../base-types';
import type { PublishedPackageInput } from '../interface-definitions/index';
import type { PackageEntitlement } from './package-entitlement';
import type { SystemTaskExecution } from './system-task-execution';

export interface NpmPackageData {
  readonly id: Id;
  readonly name?: string;
  readonly publishRepository?: string;
  readonly publishWorkflow?: string;
  readonly publishEnvironment?: string;
  readonly description?: string;
  readonly access?: PackageAccess;
  readonly deprecatedMessage?: string;
  readonly upstreamSyncedAt?: Date;
  readonly distTagsJson?: string;
  readonly category?: PackageCategory;
  readonly categoryOverride?: PackageCategory;
  readonly latestVersion?: string;
  readonly latestPublishedAt?: Date;
  readonly latestNewsSummary?: string;
  readonly hasDocs?: boolean;
  readonly hasNews?: boolean;
  readonly registryTokens?: RegistryToken[];
  readonly versions?: PackageVersion[];
  readonly packageDocs?: PackageDoc[];
}

export interface NpmPackage extends Readonly<NpmPackageData> {}
export class NpmPackage extends BusinessObjectBase {
  static readonly entityName = 'NpmPackage' as const;
  static readonly plural = 'npm-packages';
  static schema = npmPackageSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<NpmPackage[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new NpmPackage(data));
  }

  static async findOne(_filter?: any): Promise<NpmPackage | null> {
    throw new Error('findOne is not available on NpmPackage');
  }

  static async findById(id: Id, filter?: any): Promise<NpmPackage> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new NpmPackage(data);
  }

  static async create(_data: any): Promise<NpmPackage> {
    throw new Error('create is not available on NpmPackage');
  }

  static async createMany(_data: any[]): Promise<NpmPackage[]> {
    throw new Error('createMany is not available on NpmPackage');
  }

  static async createWithChildren(_data: any): Promise<NpmPackage> {
    throw new Error('createWithChildren is not available on NpmPackage');
  }

  static async createManyWithChildren(_data: any[]): Promise<NpmPackage[]> {
    throw new Error('createManyWithChildren is not available on NpmPackage');
  }

  static async updateById(_id: Id, _updates: any): Promise<NpmPackage> {
    throw new Error('updateById is not available on NpmPackage');
  }

  static async deleteById(_id: Id): Promise<boolean> {
    throw new Error('deleteById is not available on NpmPackage');
  }

  // --- Behaviors ---

  static async visiblePackage(filter?: any): Promise<NpmPackage> {
    const url = `${this.baseUrl}/${this.plural}/visible-package`;
    const queryParams = new URLSearchParams();
    if (filter !== undefined) queryParams.set('filter', JSON.stringify(filter));
    const qs = queryParams.toString();
    const fullUrl = qs ? `${url}?${qs}` : url;
    return this.get<NpmPackage>(fullUrl).then((value) =>
      decodeDecimals(npmPackageSchema, value),
    );
  }
  static async visiblePackages(filter?: any): Promise<NpmPackage[]> {
    const url = `${this.baseUrl}/${this.plural}/visible-packages`;
    const queryParams = new URLSearchParams();
    if (filter !== undefined) queryParams.set('filter', JSON.stringify(filter));
    const qs = queryParams.toString();
    const fullUrl = qs ? `${url}?${qs}` : url;
    return this.get<NpmPackage[]>(fullUrl).then((values) =>
      values?.map((value) => decodeDecimals(npmPackageSchema, value)),
    );
  }
  static async published(
    body: PublishedPackageInput,
    xNewsDraftToken: string,
  ): Promise<{ queued: boolean; name: string; version: string }> {
    const url = `${this.baseUrl}/${this.plural}/published`;
    return this.post<{ queued: boolean; name: string; version: string }>(
      url,
      { body },
      { xNewsDraftToken },
    );
  }
  static async pullVersions(
    name: string,
    versions: string[],
  ): Promise<
    { version: string; added?: boolean; unpacked?: boolean; error?: string }[]
  > {
    const url = `${this.baseUrl}/${this.plural}/pull-versions`;
    return this.post<
      { version: string; added?: boolean; unpacked?: boolean; error?: string }[]
    >(url, { name, versions });
  }
  async setCategoryOverride(
    category?: PackageCategory | null,
  ): Promise<{ id: number; category: string; categoryOverride?: string }> {
    const url = `${BusinessObjectBase.baseUrl}/${NpmPackage.plural}/${(this as any).id}/set-category-override`;
    return BusinessObjectBase.post<{
      id: number;
      category: string;
      categoryOverride?: string;
    }>(url, { category });
  }
  static async syncCatalog(packages?: string[]): Promise<any> {
    const url = `${this.baseUrl}/${this.plural}/sync-catalog`;
    return this.post<any>(url, { packages });
  }
}
